import { type IconType } from "@jsq/ds/icons";
import { PaymentMethod } from "../../../../common/enums";
import { PAYMENT_METHOD_ICON } from "../../common/constants";
import { type PaymentRailAvailability } from "../../hooks/useAvailablePaymentRails";

export type RailCardOption = {
  title: string;
  details: string;
  icon: IconType;
  value: string;
};

/**
 * Static presentation metadata for a rail code returned by `availablePaymentRails`.
 * Keeps the mapping of rail codes to the existing three-way `PaymentMethod` enum in one place so
 * the V2 selector can still drive the ACH / Wire / Check forms while surfacing the new rail names.
 */
export type RailPresentation = {
  title: string;
  details: string;
  icon: IconType;
  /**
   * The PaymentMethod the downstream form expects. Every rail code we know about resolves to a real
   * `PaymentMethod`, so this is only `undefined` for an unrecognized code — in which case the
   * selector shows the card but surfaces a "form coming soon" notice on Continue.
   */
  paymentMethod?: PaymentMethod;
};

/**
 * Rail code for the "Other" tile. Not a real rail — it is appended client-side from the caller's
 * `availablePaymentMethods` allowlist (subscription flows only). The literal lowercase form matches
 * `PaymentMethod.Other.toLowerCase()`, so back-navigation and Edit-mode rehydration — which seed
 * `selectedRail` from `selectedPaymentMethod` — round-trip with no special casing.
 */
export const OTHER_RAIL_CODE = PaymentMethod.Other.toLowerCase();

export const RAIL_PRESENTATION: Record<string, RailPresentation> = {
  ach: {
    title: "US direct deposit (ACH)",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.ACH],
    paymentMethod: PaymentMethod.ACH,
  },
  wire: {
    title: "Wire transfer",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.Wire],
    paymentMethod: PaymentMethod.Wire,
  },
  check: {
    title: "Check",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.Check],
    paymentMethod: PaymentMethod.Check,
  },
  eft: {
    title: "Canada bank transfer (EFT)",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.EFT],
    paymentMethod: PaymentMethod.EFT,
  },
  sepa: {
    title: "Europe bank transfer (SEPA)",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.SEPA],
    paymentMethod: PaymentMethod.SEPA,
  },
  bacs: {
    title: "UK bank transfer (BACS/FPS/CHAPS)",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.BACS],
    paymentMethod: PaymentMethod.BACS,
  },
  becs: {
    title: "Australia direct entry (BECS)",
    details: "",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.BECS],
    paymentMethod: PaymentMethod.BECS,
  },
  [OTHER_RAIL_CODE]: {
    title: "Other",
    details: "Custom payment instructions",
    icon: PAYMENT_METHOD_ICON[PaymentMethod.Other],
    paymentMethod: PaymentMethod.Other,
  },
};

export const toRailCardOption = (rail: PaymentRailAvailability): RailCardOption => {
  const presentation = RAIL_PRESENTATION[rail.railCode];
  // Show partial-support count when not all positions support this rail; otherwise empty.
  const partialSupport = rail.isFullySupported
    ? ""
    : `${rail.supportedCount} of ${rail.totalCount} positions support ${rail.railName}`;
  return {
    title: presentation?.title ?? rail.railName,
    details: partialSupport,
    icon: presentation?.icon ?? "university",
    value: rail.railCode,
  };
};
